import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../contexts/AppContext';
import useDeliveryService from '../../app/services/hooks/useDeliveryService';
import { Delivery, DeliveryFilters } from '../../app/models/Delivery';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import { toast } from 'react-toastify';
import useNavigationPage from '../../hooks/useNavigationPage';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';

import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import DeliveryDataTable from './components/tables/DeliveryDataTable';
import DeliveryCreate from './components/DeliveryCreate';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Entregas',
        url: '/deliveries',
        isActive: true
    }
];

const Deliveries = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const { fetchingGetDeliveries, getDeliveries, deleteDelivery } = useDeliveryService();

    // Estados
    const [deliveries, setDeliveries] = useState<Delivery[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showingCreate, setShowingCreate] = useState(false);

    // Filtros de la tabla
    const [filter, setFilter] = useState<DeliveryFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    // Recargar tabla cuando cambian los filtros
    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        try {
            getDeliveries(filter, {
                onSuccess: (response: ServiceResponse) => {
                    setDeliveries(response.data?.deliveries || []);
                    setTotalRows(response.data?.total_rows || 0);
                },
                onError: (response: ServiceResponse) => {
                    console.error('Error al cargar entregas:', response);
                    toast.error(response.message, { autoClose: 2000 });
                    setDeliveries([]);
                    setTotalRows(0);
                }
            });
        } catch (error) {
            console.error('Error en reloadTable:', error);
            setDeliveries([]);
            setTotalRows(0);
        }
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (deliveryId: number) => {
        navigationPage('/deliveries/' + deliveryId + '/edit');
    };

    const showView = (deliveryId: number) => {
        navigationPage('/deliveries/' + deliveryId + '/view');
    };

    const destroy = (deliveryId: number) => {
        const _text = 'Está a punto de eliminar la entrega #' + deliveryId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando entrega...');

                        deleteDelivery(deliveryId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La entrega no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Entregas" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nueva Entrega" />}
                />

                <DeliveryDataTable
                    deliveries={deliveries}
                    totalRows={totalRows}
                    loading={fetchingGetDeliveries}
                    edit={showEdit}
                    destroy={destroy}
                    view={showView}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Entrega"
                    backdrop={true}
                    showFooter={false}
                >
                    <DeliveryCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default Deliveries;

